// Category progress report (admin), opened from "Progress Report" on Courses Management. One tab per
// course of the category (e.g. Stories: Balgopal / Saint / 2-min Balgruha Stories), each listing the
// students it is assigned to with stories completed, progress, status and last activity, from
// GET /api/v2/lms/admin/courses/progress-report. The period picker filters by assignment date. 50 students
// a page; clicking a student opens their story-by-story popup (StudentProgressPopup).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  Download,
  Eye,
  PlayCircle,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react';
import { api } from '../../api';
import { useRBAC } from '../../contexts/RBACContext';
import LoadingState from '../../components/common/LoadingState';
import exportCategoryProgress, { fmtDate } from '../../components/adminCourses/exportCategoryProgress';
import { resultDialog } from '../../utils/swalDialogs';
import {
  CategorySidebar,
  categoryLabel,
  courseInSubcategory,
  promptAddCategory,
  subcategoryChain,
  useCourseCategories,
  useCourseSubcategories,
} from './coursesShared';
import StudentProgressPopup, { STATUS, pageList, pct } from './StudentProgressPopup';
import SearchableSelect from '../../components/common/SearchableSelect';

const PAGE_SIZE = 50;
const barColor = (p) => (p >= 70 ? '#16a34a' : p >= 50 ? '#2563eb' : p >= 25 ? '#f59e0b' : '#ef4444');
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
const apiError = (err) => err?.response?.data?.error || err?.response?.data?.message || 'Failed to load the progress report.';

// Period presets: students whose course was assigned to them in the period.
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const fromDateInput = (v) => { const [y, m, d] = v.split('-').map(Number); return new Date(y, m - 1, d); };
const toDateInput = (d) => [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
const PERIODS = [
  { key: 'all', label: 'All time' },
  { key: 'this-month', label: 'This month', range: (y, m) => [new Date(y, m, 1), new Date(y, m + 1, 0)] },
  { key: 'last-month', label: 'Last month', range: (y, m) => [new Date(y, m - 1, 1), new Date(y, m, 0)] },
  { key: 'last-3-months', label: 'Last 3 months', range: (y, m) => [new Date(y, m - 2, 1), new Date(y, m + 1, 0)] },
  { key: 'this-year', label: 'This year', range: (y) => [new Date(y, 0, 1), new Date(y, 11, 31)] },
];
function periodFor(key, now = new Date()) {
  const preset = PERIODS.find((p) => p.key === key);
  if (!preset?.range) return { key: 'all', from: null, to: null };
  const [from, to] = preset.range(now.getFullYear(), now.getMonth());
  return { key, from, to: endOfDay(to) };
}
const inPeriod = (period, student) => {
  if (!period.from) return true;
  const at = student.assignedAt ? new Date(student.assignedAt) : null;
  return Boolean(at) && at >= period.from && at <= period.to;
};

function PeriodPicker({ period, onChange }) {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState({ from: '', to: '' });
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    setCustom({ from: period.from ? toDateInput(period.from) : '', to: period.to ? toDateInput(period.to) : '' });
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const label = period.from ? `${fmtDate(period.from)} - ${fmtDate(period.to)}` : 'All time';
  const customValid = Boolean(custom.from && custom.to && custom.from <= custom.to);
  const pick = (next) => { onChange(next); setOpen(false); };
  return (
    <div className="pr-period" ref={ref}>
      <button type="button" className="pr-period__btn" aria-haspopup="true" aria-expanded={open} aria-label={`Assigned period: ${label}`} onClick={() => setOpen((o) => !o)}>
        <CalendarDays size={18} />
        <span>{label}</span>
        <ChevronDown size={16} />
      </button>
      {open && (
        <div className="pr-period__menu">
          <p>Shows the students the course was assigned to in this period.</p>
          {PERIODS.map((p) => (
            <button key={p.key} type="button" className={period.key === p.key ? 'is-active' : ''} onClick={() => pick(periodFor(p.key))}>{p.label}</button>
          ))}
          <div className="pr-period__custom">
            <b>Custom range</b>
            <label>From<input type="date" value={custom.from} max={custom.to || undefined} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} /></label>
            <label>To<input type="date" value={custom.to} min={custom.from || undefined} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} /></label>
            <button
              type="button"
              className="cm-btn cm-btn--purple cm-btn--sm"
              disabled={!customValid}
              onClick={() => pick({ key: 'custom', from: fromDateInput(custom.from), to: endOfDay(fromDateInput(custom.to)) })}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ tone, icon, value, label, note, bar }) {
  return (
    <div className={`cm-stat cm-stat--${tone} pr-stat`} role="group" aria-label={label}>
      <span className="cm-stat__icon">{icon}</span>
      <div>
        <b>{value}</b>
        <span>{label}</span>
        <small>{note}</small>
        {bar && <span className="pr-stat__bar" aria-hidden="true"><i style={{ width: `${bar.percent}%`, background: bar.color }} /></span>}
      </div>
    </div>
  );
}

/**
 * "<Category> – Progress Report" (admin). SECURITY: requires 'LMS Management' 'Manage' (route and API).
 * The category, subcategory picked in the sidebar and course tab live in the link
 * (?category=English&sub=action-songs&course=<id>); a subcategory narrows the tabs to its courses.
 */
export default function CourseProgressReport() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isLoading: rbacLoading, permissions } = useRBAC();
  const { categories, loading: categoriesLoading, reload: reloadCategories } = useCourseCategories();
  const { nodesFor } = useCourseSubcategories();
  const category = searchParams.get('category') || '';
  const sub = searchParams.get('sub') || '';
  const courseParam = searchParams.get('course') || '';

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(() => periodFor('all'));
  const [balagruha, setBalagruha] = useState('');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);
  const [page, setPage] = useState(1);
  const requestRef = useRef(0);

  // SECURITY CHECK: redirect unauthorized users
  useEffect(() => {
    if (rbacLoading || !Object.keys(permissions).length) return;
    if (!hasPermission('LMS Management', 'Manage')) navigate('/access-denied');
  }, [hasPermission, navigate, rbacLoading, permissions]);

  // Opened without a category: show the first one that has courses.
  useEffect(() => {
    if (category || categoriesLoading) return;
    const first = categories.find((c) => c.courseCount > 0) || categories[0];
    if (first) setSearchParams({ category: first.name }, { replace: true });
  }, [category, categories, categoriesLoading, setSearchParams]);

  const load = useCallback(async () => {
    if (!category) return;
    const request = ++requestRef.current;
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/api/v2/lms/admin/courses/progress-report', { params: { category } });
      if (request === requestRef.current) setReport(res.data?.data || { category, courses: [] });
    } catch (err) {
      if (request === requestRef.current) { setReport(null); setError(apiError(err)); }
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    if (!rbacLoading && hasPermission('LMS Management', 'Manage')) load();
  }, [rbacLoading, hasPermission, load]);

  useEffect(() => { setBalagruha(''); setViewing(null); }, [category]);

  const isStories = /stor/i.test(category);
  const unit = isStories ? 'Stories' : 'Chapters';
  // The picked subcategory and the levels above it (English > Action Songs > Vocal Songs).
  const chain = useMemo(() => (sub ? subcategoryChain(nodesFor(category), sub) : []), [nodesFor, category, sub]);
  const subcategoryNode = chain.length ? chain[chain.length - 1] : null;
  const where = [categoryLabel(category), ...chain.map((node) => node.label)].join(' › ');
  const here = subcategoryNode?.label || categoryLabel(category);
  const courses = useMemo(
    () => (report?.category === category ? report.courses : []).filter((c) => !subcategoryNode || courseInSubcategory(c, subcategoryNode)),
    [report, category, subcategoryNode]
  );
  const course = courses.find((c) => c._id === courseParam) || courses[0] || null;

  const balagruhaOptions = useMemo(
    () => [...new Set(courses.flatMap((c) => c.students.flatMap((s) => s.balagruhas)))].sort((a, b) => a.localeCompare(b)),
    [courses]
  );
  const q = search.trim().toLowerCase();
  const inScope = useCallback(
    (s) => inPeriod(period, s) && (!balagruha || s.balagruhas.includes(balagruha)),
    [period, balagruha]
  );
  const matches = useCallback(
    (s) => inScope(s) && (status === 'all' || s.status === status) && (!q || s.name.toLowerCase().includes(q)),
    [inScope, status, q]
  );
  const scoped = useMemo(() => (course?.students || []).filter(inScope), [course, inScope]);
  const rows = useMemo(() => scoped.filter(matches), [scoped, matches]);
  const total = scoped.length;
  const count = (key) => scoped.filter((s) => s.status === key).length;
  const completed = count('completed');
  const inProgress = count('in_progress');
  const notStarted = count('not_started');

  // 50 students a page; back to the first page whenever the list changes.
  useEffect(() => setPage(1), [course?._id, period, balagruha, status, q]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const offset = (currentPage - 1) * PAGE_SIZE;
  const pageRows = rows.slice(offset, offset + PAGE_SIZE);

  const selectCategory = (name) => { if (name && (name !== category || sub)) setSearchParams({ category: name }); };
  // Picking the selected subcategory again goes back to the whole category.
  const selectSubcategory = (name, node) =>
    setSearchParams(name === category && node.key === sub ? { category: name } : { category: name, sub: node.key });
  const selectCourse = (id) => setSearchParams({ category, ...(sub && { sub }), course: id }, { replace: true });

  const exportReport = () => {
    try {
      exportCategoryProgress({ category, unit, sheets: courses.map((c) => ({ course: c, students: c.students.filter(matches) })) });
    } catch {
      resultDialog('error', 'Could not export the report', 'Please try again.');
    }
  };

  if (rbacLoading) return <LoadingState message="Loading permissions..." fullScreen />;

  const emptyMessage = !course
    ? ''
    : !course.students.length
      ? `No students are assigned to ${course.title} yet.${course.status !== 'published' ? ' Publish it, then assign it from Courses Management.' : ''}`
      : !rows.length
        ? 'No students match these filters.'
        : '';

  return (
    <div className="cm-page pr-page">
      <div className="cm-layout">
        <CategorySidebar
          categories={categories}
          selected={category}
          onSelect={selectCategory}
          onAddCategory={async () => { if (await promptAddCategory(categories)) reloadCategories(); }}
          subcategoryNodes={nodesFor}
          selectedSubcategory={sub}
          onSelectSubcategory={selectSubcategory}
        />

        <main className="cm-main">
          <header className="pr-header">
            <button type="button" className="cm-btn cm-btn--ghost pr-back" onClick={() => navigate('/admin/courses')}><ArrowLeft size={18} />Back</button>
            <div className="cm-title">
              <span className="cm-title__icon" aria-hidden="true"><BookOpen size={28} /></span>
              <div>
                <h1>{category ? `${where} – Progress Report` : 'Progress Report'}</h1>
                <p>{isStories && !subcategoryNode ? 'Student-wise progress for all story categories' : `Student-wise progress for all ${here || 'course'} courses`}</p>
              </div>
            </div>
            <div className="pr-header__tools">
              <PeriodPicker period={period} onChange={setPeriod} />
              <button type="button" className="cm-btn cm-btn--purple" onClick={exportReport} disabled={!courses.length}><Download size={18} />Export Report</button>
            </div>
          </header>

          {loading && !courses.length ? (
            <section className="cm-card"><LoadingState message="Loading progress report..." /></section>
          ) : error ? (
            <section className="cm-card cv-error">
              <AlertCircle size={28} />
              <p>{error}</p>
              <div>
                <button type="button" className="cm-btn cm-btn--purple cm-btn--sm" onClick={load}><RefreshCw size={15} />Try again</button>
              </div>
            </section>
          ) : !category ? (
            <section className="cm-card cv-empty"><p>No course categories yet.</p></section>
          ) : !courses.length ? (
            <section className="cm-card cv-empty">
              <p>There are no courses in {where} yet.</p>
              <small>Create one from Courses Management, then assign it to students.</small>
            </section>
          ) : (
            <>
              <div className="pr-tabs" role="tablist" aria-label={`${here} courses`}>
                {courses.map((c) => {
                  const active = c._id === course._id;
                  return (
                    <button
                      key={c._id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      className={`pr-tab${active ? ' is-active' : ''}`}
                      onClick={() => selectCourse(c._id)}
                      title={c.status !== 'published' ? `${c.title} (${c.status})` : c.title}
                    >
                      <span className="pr-tab__icon" aria-hidden="true">{c.icon || '📚'}</span>
                      <span className="pr-tab__name">{c.title}</span>
                      {c.status !== 'published' && <small className="pr-tab__status">{c.status}</small>}
                    </button>
                  );
                })}
              </div>

              <section className="cm-stats" aria-label={`${course.title} summary`}>
                <StatCard tone="blue" icon={<Users size={26} />} value={total} label="Total Students Assigned" note={`(${course.title})`} />
                <StatCard tone="green" icon={<CheckCircle2 size={26} />} value={completed} label="Completed" note={`${pct(completed, total)}% of assigned students`} bar={{ percent: pct(completed, total), color: '#16a34a' }} />
                <StatCard tone="amber" icon={<PlayCircle size={26} />} value={inProgress} label="In Progress" note={`${pct(inProgress, total)}% of assigned students`} bar={{ percent: pct(inProgress, total), color: '#f59e0b' }} />
                <StatCard tone="pink" icon={<AlertCircle size={26} />} value={notStarted} label="Not Started" note={`${pct(notStarted, total)}% of assigned students`} bar={{ percent: pct(notStarted, total), color: '#ef4444' }} />
              </section>

              <section className="pr-filters" aria-label="Filters">
                <label className="pr-filter">
                  <span>Balagruha</span>
                  <SearchableSelect searchPlaceholder="Search balagruha..." value={balagruha} onChange={(e) => setBalagruha(e.target.value)} aria-label="Balagruha">
                    <option value="">All Balagruhas</option>
                    {balagruhaOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                  </SearchableSelect>
                </label>
                <label className="pr-filter">
                  <span>Progress Status</span>
                  <select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Progress Status">
                    <option value="all">All Students</option>
                    <option value="completed">Completed</option>
                    <option value="in_progress">In Progress</option>
                    <option value="not_started">Not Started</option>
                  </select>
                </label>
                <label className="cm-search pr-search">
                  <Search size={16} />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search student name..." aria-label="Search student name" />
                </label>
              </section>

              <section className="cm-card">
                <div className="cm-card__head">
                  <h2 className="pr-card__title"><BookOpen size={22} aria-hidden="true" />{course.title} - Student Progress ({plural(rows.length, 'Student')})</h2>
                </div>
                <div className="cm-table-wrap">
                  <table className="cm-table pr-table">
                    <thead>
                      <tr>
                        <th className="is-num">Sr. No.</th>
                        <th>Student Name</th>
                        <th>Balagruha</th>
                        <th className="is-num">{unit} Completed<small>(out of total {course.stories.length})</small></th>
                        <th>Progress</th>
                        <th className="is-num">Status</th>
                        <th>Last Active</th>
                        <th className="is-num">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emptyMessage ? (
                        <tr><td colSpan={8} className="cm-empty">{emptyMessage}</td></tr>
                      ) : pageRows.map((s, i) => {
                        const state = STATUS[s.status] || STATUS.not_started;
                        return (
                          <tr key={s._id} className="pr-row" onClick={() => setViewing(s)}>
                            <td className="is-num">{offset + i + 1}</td>
                            <td><button type="button" className="pr-student" title="Open story-by-story progress">{s.name}</button></td>
                            <td className="pr-muted">{s.balagruhas.join(', ') || '-'}</td>
                            <td className="is-num" title={s.storyIds ? `Assigned ${s.storiesTotal} of ${course.stories.length}` : undefined}>
                              {s.storiesCompleted} / {s.storiesTotal}
                            </td>
                            <td>
                              <div className="cm-progress pr-progress">
                                <span className="cm-progress__bar"><i style={{ width: `${s.progress}%`, background: barColor(s.progress) }} /></span>
                                <b>{s.progress}%</b>
                              </div>
                            </td>
                            <td className="is-num"><span className={`cm-pill cm-pill--${state.tone} pr-status`}>{state.label}</span></td>
                            <td>{fmtDate(s.lastActive)}</td>
                            <td className="is-num">
                              <button type="button" className="pr-view" aria-label={`View ${s.name}'s progress`}><Eye size={15} />View</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {pages > 1 && (
                  <footer className="cm-pager">
                    <span>Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, rows.length)} of {rows.length}</span>
                    <nav aria-label="Student pages">
                      <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} aria-label="Previous page">‹</button>
                      {pageList(currentPage, pages).map((p, i) => (p === '…'
                        ? <span key={`gap-${i}`} className="pr-pager__gap">…</span>
                        : <button key={p} type="button" className={p === currentPage ? 'is-active' : ''} aria-current={p === currentPage ? 'page' : undefined} onClick={() => setPage(p)}>{p}</button>))}
                      <button type="button" disabled={currentPage === pages} onClick={() => setPage(currentPage + 1)} aria-label="Next page">›</button>
                    </nav>
                  </footer>
                )}
              </section>
            </>
          )}
        </main>
      </div>

      {viewing && course && (
        <StudentProgressPopup
          student={viewing}
          category={category}
          courses={courses}
          initialCourseId={course._id}
          onClose={() => setViewing(null)}
          onOpenProfile={() => navigate(`/admin/students/${viewing._id}`)}
        />
      )}
    </div>
  );
}
